import { Suspense, useEffect, useState, type ComponentType, type ReactElement } from "react";
import { lazyComponent, type Load } from "../shared/lazy.js";
import { keep, keepFor, kept } from "../shared/keep.js";
import { api, ApiError, prefetch, warmEditor, type Me as MeData } from "../shared/api.js";
import { useProject } from "../features/projects/store.js";
import { onSession, type Session } from "./auth.js";
import { devMode } from "./env.js";
import { ErrorBanner, Loading } from "../shared/common.js";
import { AppContext, useApp, type WorkspaceActions } from "../shared/context.js";
import { match, navigate, useLocation } from "./navigation.js";
import { Login } from "../features/account/Login.js";
import { Privacy } from "../features/account/Privacy.js";
import { Shell } from "./Shell.js";
import { SPACES } from "./spaces.js";
import { Me } from "../features/account/Settings.js";
import { loadContentSettings } from "../features/account/content.js";
import { pickWorkspace, rememberWorkspace, savedWorkspace } from "./workspace.js";

/**
 * Chaque écran est un morceau chargé à la demande : l'accueil n'attend pas le code de l'éditeur,
 * du carrousel ou des idées. Le morceau de l'écran demandé part dès le démarrage, en même temps
 * que la connexion (sinon il attendrait /me) ; les autres, quand le navigateur est libre, pour
 * qu'aller d'un écran à l'autre reste instantané.
 */
const chunks: Load[] = [];
function screen<M extends Record<K, ComponentType<never>>, K extends keyof M>(importer: () => Promise<M>, name: K) {
  const C = lazyComponent(importer, name);
  chunks.push(C.load);
  return C;
}
let preloaded = false;
function preloadScreens() {
  if (preloaded) return;
  preloaded = true;
  const idle = (cb: () => void) => ("requestIdleCallback" in window ? requestIdleCallback(cb, { timeout: 5000 }) : setTimeout(cb, 1500));
  setTimeout(() => idle(() => chunks.forEach((load) => void load().catch(() => undefined))), 2000);
}

const Projects = screen(() => import("../features/projects/Projects.js"), "Projects");
const Inspiration = screen(() => import("../features/inspiration/Inspiration.js"), "Inspiration");
const Library = screen(() => import("../features/library/Library.js"), "Library");
const Ideas = screen(() => import("../features/studio/Ideas.js"), "Ideas");
const ReadyScreen = screen(() => import("../features/projects/Ready.js"), "ReadyScreen");
const NewProject = screen(() => import("../features/create/NewProject.js"), "NewProject");
const ProjectScreen = screen(() => import("../features/projects/Project.js"), "ProjectScreen");
const NewStoryboard = screen(() => import("../features/storyboard/Storyboard.js"), "NewStoryboard");
const StoryboardScreen = screen(() => import("../features/storyboard/Storyboard.js"), "StoryboardScreen");
const InstantStart = screen(() => import("../features/instant/Instant.js"), "InstantStart");
const NewCarousel = screen(() => import("../features/carousel/NewCarousel.js"), "NewCarousel");
// Les Réglages n'apparaissent qu'avec « Ton contenu » (son propre morceau) : sinon il arrivait après et poussait la page.
const Settings = screen(() => loadContentSettings().then(() => ({ Me })), "Me");
const MemoryScreen = screen(() => import("../features/memory/Memory.js"), "MemoryScreen");
const CarouselScreen = screen(() => import("../features/carousel/Carousel.js"), "CarouselScreen");
const PublishScreen = screen(() => import("../features/social/Publish.js"), "PublishScreen");
const Inbox = screen(() => import("../features/social/Inbox.js"), "Inbox");
const ScriptStart = screen(() => import("../features/script/Script.js"), "ScriptStart");
const ScriptScreen = screen(() => import("../features/script/Script.js"), "ScriptScreen");
const SharedTemplate = screen(() => import("../features/library/Reference.js"), "SharedTemplate");

/**
 * Les écrans (doc 12 §12.2) derrière la connexion. Les anciennes adresses (import, brief,
 * retours, templates, marque) mènent au bon endroit de la nouvelle organisation : un lien
 * partagé ou un favori du navigateur ne casse pas.
 */
type Route = [string, (p: Record<string, string>, search: string) => ReactElement, Load?];
const routes: Route[] = [
  ["/", () => <Projects />, Projects.load],
  ["/inspiration", () => <Inspiration />, Inspiration.load],
  ["/bibliotheque", () => <Library />, Library.load],
  // Un template partagé par quelqu'un : il rejoint les templates de l'espace ouvert, puis joue.
  ["/t/:code", (p) => <SharedTemplate key={p.code} code={p.code} />, SharedTemplate.load],
  ["/idees", () => <Ideas />, Ideas.load],
  ["/commentaires", () => <Inbox />, Inbox.load],
  ["/moi", () => <Settings />, Settings.load],
  ["/moi/memoire", () => <MemoryScreen />, MemoryScreen.load],
  ["/nouveau/storyboard", () => <NewStoryboard />, NewStoryboard.load],
  ["/nouveau/carrousel", () => <NewCarousel />, NewCarousel.load],
  ["/nouveau/script", () => <ScriptStart />, ScriptStart.load],
  ["/m/:id/script", (p) => <ScriptScreen key={p.id} id={p.id} />, ScriptScreen.load],
  ["/nouveau/instant/:id", (p) => <InstantStart key={p.id} templateId={p.id} />, InstantStart.load],
  ["/m/:id/carrousel", (p) => <CarouselScreen key={p.id} id={p.id} />, CarouselScreen.load],
  ["/m/:id/storyboard", (p) => <StoryboardScreen key={p.id} id={p.id} />, StoryboardScreen.load],
  ["/nouveau", () => <NewProject />, NewProject.load],
  ["/m/:id/nouveau", (p) => <NewProject id={p.id} />, NewProject.load],
  ["/m/:id/rushes", (p) => <ProjectScreen key={p.id} id={p.id} segment="rushes" />, ProjectScreen.load],
  ["/m/:id/montage", (p) => <ProjectScreen key={p.id} id={p.id} segment="montage" />, ProjectScreen.load],
  ["/m/:id/export", (p) => <ProjectScreen key={p.id} id={p.id} segment="export" />, ProjectScreen.load],
  ["/m/:id/publier", (p) => <PublishScreen key={p.id} id={p.id} />, PublishScreen.load],
  // « Vidéo prête » (audit §4.6) : un montage pas encore prêt y reprend là où il en est.
  ["/m/:id", (p) => <ReadyScreen key={p.id} id={p.id} />, ReadyScreen.load],
  // Les anciennes adresses. Le Studio est devenu Idées ; son profil et son compte, Réglages › Ton contenu.
  ["/studio", (_p, s) => <Redirect to={/rayon=profil/.test(s) ? "/moi#contenu" : "/idees"} />],
  ["/m/:id/import", (p) => <Redirect to={`/m/${p.id}/nouveau`} />],
  ["/m/:id/brief", (p) => <Redirect to={`/m/${p.id}/nouveau?style=1`} />],
  ["/m/:id/retours", (p, s) => <Redirect to={`/m/${p.id}/montage${s}`} />],
  ["/templates", () => <Redirect to="/inspiration" />],
  ["/marque", () => <Redirect to="/bibliotheque?rayon=kits" />],
];

// Le code de l'écran ouvert part tout de suite, sans attendre la session.
for (const [pattern, , load] of routes) {
  if (!match(pattern, location.pathname)) continue;
  void load?.().catch(() => undefined);
  break;
}

export function App() {
  const { path, search } = useLocation();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [me, setMe] = useState<{ me: MeData | null; workspaceId: string | null; error?: unknown } | null>(null);
  const [n, setN] = useState(0);

  useEffect(() => onSession(setSession), []);

  useEffect(() => {
    if (!session) return setMe(null);
    keepFor(session.uid);
    let alive = true;
    // L'accueil d'un habitué : ses vidéos et son Studio partent en même temps que /me (l'espace de la
    // dernière visite est connu), au lieu d'attendre la réponse — un aller-retour de moins vers la base.
    const last = n === 0 && location.pathname === "/" ? savedWorkspace(session.uid) : null;
    if (last) {
      prefetch(`/v1/workspaces/${encodeURIComponent(last)}/projects`);
      prefetch(`/v1/workspaces/${last}/studio`);
    }
    // Un projet ouvert par son adresse : lu dès maintenant, une fois pour l'espace (ProjectWorkspace) et son écran (le
    // magasin des projets partage la lecture), dont le code arrive après. L'éditeur rouvert sur une version (`?v=`)
    // demande aussi tout de suite sa version, sa timeline, sa vidéo et ses versions, sans attendre le projet.
    const opened = n === 0 ? /^\/m\/([^/]+)(\/[^/]+)?/.exec(location.pathname) : null;
    if (opened) {
      const id = decodeURIComponent(opened[1]);
      prefetch(`/v1/projects/${encodeURIComponent(id)}`);
      const v = Number(new URLSearchParams(location.search).get("v"));
      if (opened[2] === "/montage" && Number.isInteger(v) && v > 0) warmEditor(id, v);
    }
    // L'app s'ouvre sur le profil gardé de la dernière visite (ses espaces) : les écrans demandent leurs
    // données sans attendre /me, qui revient 300 à 500 ms plus tard et met le profil à jour.
    const prior = n === 0 ? kept<MeData>("me") : undefined;
    if (prior) setMe({ me: prior, workspaceId: pickWorkspace(prior.workspaces, savedWorkspace(prior.uid)) });
    api.me().then(
      (m) => {
        // Firebase a rendu un autre compte que la session affichée : sa réponse ne se range pas sous l'ancien.
        if (!alive || (!session.dev && m.uid !== session.uid)) return;
        keep("me", m);
        // L'espace retenu à la dernière visite (par compte), s'il existe encore.
        const workspaceId = pickWorkspace(m.workspaces, savedWorkspace(m.uid));
        rememberWorkspace(m.uid, workspaceId);
        setMe({ me: m, workspaceId });
      },
      (e) => {
        if (!alive) return;
        // En local, l'espace « dev » existe toujours (CONTRACT.md) : on s'en sert si /me manque encore.
        if (devMode && e instanceof ApiError && e.missing) setMe({ me: null, workspaceId: "dev" });
        // Hors ligne un instant : le profil gardé suffit, chaque écran dira lui-même s'il ne charge pas.
        else if (prior && e instanceof ApiError && e.status === 0) return;
        else setMe({ me: null, workspaceId: null, error: e });
      },
    );
    return () => {
      alive = false;
    };
  }, [session, n]);

  useEffect(() => {
    if (path === "/confidentialite" || path === "/privacy") return;
    if (session === null && path !== "/login")
      navigate("/login?next=" + encodeURIComponent(location.pathname + location.search), { replace: true });
    if (session && path === "/login") navigate(new URLSearchParams(location.search).get("next") ?? "/", { replace: true });
  }, [session, path]);

  // Lisible sans compte : on doit savoir ce qu'on fait des vidéos avant de s'inscrire.
  if (path === "/confidentialite" || path === "/privacy") return <Privacy />;
  if (session === undefined) return <Loading />;
  if (session === null || path === "/login") return <Login />;
  if (!me) return <Loading />;
  if (me.error)
    return (
      <main className="page">
        <ErrorBanner error={me.error} onRetry={() => setN((x) => x + 1)} />
      </main>
    );

  // Toujours sur le profil le plus récent (setMe fonctionnel) : une réponse lente ne doit pas remettre
  // le profil gardé de la dernière visite par-dessus celui que /me vient de rendre.
  const workspaces: WorkspaceActions = {
    choose: (id) =>
      setMe((cur) => {
        if (!cur?.me?.workspaces.some((w) => w.id === id)) return cur;
        rememberWorkspace(cur.me.uid, id);
        return { ...cur, workspaceId: id };
      }),
    upsert: (w, select = false) =>
      setMe((cur) => {
        if (!cur?.me) return cur;
        const list = cur.me.workspaces.some((x) => x.id === w.id)
          ? cur.me.workspaces.map((x) => (x.id === w.id ? w : x))
          : [...cur.me.workspaces, w];
        if (select) rememberWorkspace(cur.me.uid, w.id);
        return { ...cur, me: { ...cur.me, workspaces: list }, workspaceId: select ? w.id : cur.workspaceId };
      }),
  };

  for (const [pattern, render] of routes) {
    const params = match(pattern, path);
    if (!params) continue;
    const content = <Suspense fallback={<Loading />}>{render(params, search)}</Suspense>;
    preloadScreens();
    return (
      <AppContext.Provider value={{ session, me: me.me, workspaceId: me.workspaceId, workspaces }}>
        {SPACES.includes(path) ? (
          <Shell path={path}>
            {/* Changer d'espace remonte l'écran : chaque liste se relit dans le nouvel espace, sans reste de l'ancien. */}
            <div key={`${path}|${me.workspaceId}`} className="screen">
              {content}
            </div>
          </Shell>
        ) : (
          <div key={params.id ?? path} className="screen">
            {params.id && <ProjectWorkspace id={params.id} />}
            {content}
          </div>
        )}
      </AppContext.Provider>
    );
  }
  return <Redirect to="/" />;
}

/**
 * Un projet ouvert vit dans SON espace : ouvert par un lien alors qu'un autre espace est choisi, il
 * y ramène le sélecteur, pour que ses kits, sa bibliothèque et ses chiffres soient les siens.
 */
function ProjectWorkspace({ id }: { id: string }) {
  const { workspaceId, workspaces } = useApp();
  // Le projet partagé avec son écran (une seule lecture) ; l'écran dit lui-même s'il est introuvable.
  const owner = useProject(id, { live: false }).project?.workspaceId;
  useEffect(() => {
    if (owner && owner !== workspaceId) workspaces?.choose(owner);
    // L'espace peut changer après coup (le profil gardé, puis /me) : la vérification se refait alors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, workspaceId]);
  return null;
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}
